import { useOrdersConfig } from '../config';
import { TableColumn } from '../consts';
import useFilters from '../hooks/useFilters';
import { Listbox, Transition } from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import { getUserInfo } from 'api/requests/core/users';
import { getDgcOrders, getDgcOrdersForExport } from 'api/requests/dgc/dgcOrders';
import { DgcOrder } from 'api/types/dgc/dgcOrder';
import { DropdownIcon, ExportIcon } from 'assets/svg';
import { useState } from 'react';
import { DGC_EXPORT_ROW_NUM } from 'shared/consts/dgcOrders';
import { Role } from 'shared/consts/role';
import { toast } from 'shared/hooks/useToast';
import { queryKeyBuilder } from 'shared/query';
import { hasAnyOfRole } from 'shared/utils/auth';
import * as XLSX from 'xlsx';

const EXPORT_ERROR_MESSAGE = 'Export failed. Please try again.';

function toSheetRows(orders: DgcOrder[], columns: TableColumn[]) {
  return orders.map((order) => {
    const row: Record<string, unknown> = {};
    columns.forEach(({ headerLabel, key }) => {
      row[headerLabel] = (order as unknown as Record<string, unknown>)[key];
    });
    return row;
  });
}

// idx is 0-based; the label and row range shown to the user are both 1-based, with the last chunk clamped to total.
export function buildChunkLabel(idx: number, total: number): string {
  const startRow = idx * DGC_EXPORT_ROW_NUM + 1;
  const endRow = Math.min((idx + 1) * DGC_EXPORT_ROW_NUM, total);
  return `File ${idx + 1} (Rows ${startRow}-${endRow})`;
}

function ExportButton() {
  const urlQueryParams = useFilters();
  const { columns, exportFilePrefix } = useOrdersConfig();
  const [isExporting, setIsExporting] = useState(false);

  const { data: userData } = useQuery({
    queryKey: queryKeyBuilder.currentUser(),
    queryFn: getUserInfo,
  });
  const canExport = hasAnyOfRole(userData, Role.GC_Export);

  const { data: exportedOrdersData, isPending: isProbePending } = useQuery({
    // Same query the table itself makes (Orders.tsx) — reuses its cached total instead of
    // hitting the export endpoint just to size the button before the user has clicked anything.
    queryKey: queryKeyBuilder.dgcOrdersToShow(urlQueryParams),
    queryFn: () => getDgcOrders(urlQueryParams),
    enabled: canExport,
  });

  const total = exportedOrdersData?.total ?? 0;
  const chunkNumber = Math.ceil(total / DGC_EXPORT_ROW_NUM);
  // Until the current filters' total has loaded, chunkNumber is 0 and a click would export only the first page.
  const isDisabled = isExporting || isProbePending || total === 0;

  const exportChunk = async (pageNum: number) => {
    setIsExporting(true);
    try {
      const { orders } = await getDgcOrdersForExport({
        ...urlQueryParams,
        pageNum: pageNum.toString(),
        pageSize: DGC_EXPORT_ROW_NUM.toString(),
      });
      const worksheet = XLSX.utils.json_to_sheet(toSheetRows(orders, columns), {
        header: columns.map((column) => column.headerLabel),
      });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'data');
      XLSX.writeFile(workbook, `${exportFilePrefix}-${new Date().toJSON()}.xlsx`, { compression: true });
    } catch (error) {
      toast({ title: EXPORT_ERROR_MESSAGE, variant: 'error' });
    } finally {
      setIsExporting(false);
    }
  };

  if (!canExport) {
    return null;
  }

  if (chunkNumber <= 1) {
    return (
      <button
        className="group text-white bg-palette-burgundy flex px-4 py-3 items-center rounded-full whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={isDisabled}
        onClick={() => exportChunk(1)}
      >
        <ExportIcon className="transition-transform group-hover:scale-110 w-6 h-6 flex-none" />
        <span className="ml-2 mr-4 tracking-tight text-base font-semibold">Export Results</span>
      </button>
    );
  }

  return (
    <Listbox as="div" className="relative max-w-56" disabled={isDisabled}>
      {({ open }) => (
        <>
          <Listbox.Button className="group text-white bg-palette-burgundy flex px-4 py-3 items-center rounded-full disabled:opacity-50 disabled:cursor-not-allowed">
            <ExportIcon className="transition-transform group-hover:scale-110 w-6 h-6" />
            <span className="ml-2 mr-4 tracking-tight text-base font-semibold">Export Results</span>
            <DropdownIcon className={`transition-transform ${open ? 'rotate-180' : 'rotate-0'} w-3 h-3`} />
          </Listbox.Button>
          <Transition
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
            className="w-full"
          >
            <Listbox.Options className="bg-white flex flex-col rounded-lg px-1.5 pt-5 pb-4 absolute right-0 mt-1 z-20 w-max max-w-80">
              <span className="text-xs text-palette-secondary tracking-tight px-4 mb-4">
                Due to the size of your data set, it couldn't be exported as a single file. We have generated multiple
                files for you to download.
              </span>
              <div className="max-h-52 overflow-auto scrollbar flex flex-col gap-0.5 w-full max-w-full px-1.5">
                {Array.from({ length: chunkNumber }, (_, idx) => idx).map((idx) => (
                  <Listbox.Option
                    className="font-normal rounded transition-colors text-sm tracking-tight flex items-center text-palette-secondary px-2.5 py-2.5 w-full max-w-full cursor-pointer hover:bg-palette-pinkish-gray aria-disabled:pointer-events-none aria-disabled:opacity-50"
                    key={idx}
                    value={idx}
                    aria-disabled={isDisabled}
                    onClick={() => !isDisabled && exportChunk(idx + 1)}
                  >
                    <span className="text-sm underline truncate break-words">{buildChunkLabel(idx, total)}</span>
                  </Listbox.Option>
                ))}
              </div>
            </Listbox.Options>
          </Transition>
        </>
      )}
    </Listbox>
  );
}

export default ExportButton;
